/**
 * Performance Monitoring Integration Layer
 *
 * This module provides retry-aware performance tracking that maintains accurate
 * metrics throughout the complete request lifecycle, including retries, circuit
 * breaker events, and authentication flows. It integrates with the unified
 * request coordinator to provide comprehensive performance insights.
 */

import { dayjs } from '../../../utils/date';
import { appEnv } from '../../../utils/env';
import { calculateDuration } from '../core/request.tracker';

import type {
  CircuitBreakerState,
  RequestContext,
  RetryAttempt,
} from '../../../types/api';
import type {
  IPerformanceIntegration,
  PerformanceIntegrationConfig,
  DetailedPerformanceMetrics,
  PerformanceSnapshot,
  IntegrationRetryAttemptMetrics,
  IntegrationCircuitBreakerEvent,
  IntegrationAuthEvent,
  IntegrationNetworkMetrics,
} from '../../../types/api/integration.types';

// =============================================================================
// DEFAULT CONFIGURATION
// =============================================================================

const DEFAULT_PERFORMANCE_CONFIG: PerformanceIntegrationConfig = {
  enableDetailedMetrics: appEnv('development'),
  enableRetryTracking: true,
  enableCircuitBreakerMetrics: true,
  enableTimelineReconstruction: true,
  memoryEfficientStorage: true,
  maxStoredRequests: 1000,
  metricsRetentionMs: 5 * 60 * 1000, // 5 minutes
  performanceThresholds: {
    warning: 3000, // 3 seconds
    critical: 10000, // 10 seconds
  },
};

// =============================================================================
// PERFORMANCE INTEGRATION CLASS
// =============================================================================

/**
 * Performance Monitoring Integration Layer implementation.
 *
 * This class implements the IPerformanceIntegration interface from the centralized types,
 * providing retry-aware performance tracking that maintains accurate metrics throughout
 * the complete request lifecycle.
 */
export class PerformanceIntegration implements IPerformanceIntegration {
  private config: PerformanceIntegrationConfig;
  private metricsStorage: Map<string, DetailedPerformanceMetrics>;
  private responseTimes: number[];
  private snapshot: PerformanceSnapshot;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(config: Partial<PerformanceIntegrationConfig> = {}) {
    this.config = { ...DEFAULT_PERFORMANCE_CONFIG, ...config };
    this.metricsStorage = new Map();
    this.responseTimes = [];

    this.snapshot = {
      timestamp: dayjs().valueOf(),
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      retriedRequests: 0,
      averageResponseTime: 0,
      p50ResponseTime: 0,
      p95ResponseTime: 0,
      p99ResponseTime: 0,
      circuitBreakerTrips: 0,
      authRefreshCount: 0,
      deduplicationSavings: 0,
    };

    this.startCleanupTimer();
  }

  /**
   * Track request start
   */
  public trackRequestStart(context: RequestContext): void {
    const metrics: DetailedPerformanceMetrics = {
      requestId: context.id,
      startTime: context.startTime,
      retryCount: context.retryCount,
      retryAttempts: [],
      circuitBreakerEvents: [],
      authenticationEvents: [],
      timeline: [
        {
          timestamp: context.startTime,
          type: 'request_start',
          metadata: {
            method: context.config.method,
            url: context.config.url,
            retryCount: context.retryCount,
          },
        },
      ],
    };

    this.metricsStorage.set(context.id, metrics);
    this.snapshot.totalRequests++;

    this.log('Request started', { id: context.id, url: context.config.url });
  }

  /**
   * Track request end with response
   */
  public trackRequestEnd(context: RequestContext, response?: unknown): void {
    const metrics = this.metricsStorage.get(context.id);
    if (!metrics) return;

    const endTime = dayjs().valueOf();
    const duration = calculateDuration(metrics.startTime, endTime);

    // Update metrics
    metrics.endTime = endTime;
    metrics.duration = duration;
    metrics.responseSize = this.calculateResponseSize(response);

    // Add timeline event
    metrics.timeline.push({
      timestamp: endTime,
      type: 'request_end',
      duration,
      metadata: {
        status: (response as { status?: number })?.status,
        size: metrics.responseSize,
      },
    });

    // Update snapshot
    if (
      response &&
      (response as { status?: number }).status &&
      (response as { status: number }).status < 400
    ) {
      this.snapshot.successfulRequests++;
    } else {
      this.snapshot.failedRequests++;
    }

    // Track response times for percentile calculations
    this.responseTimes.push(duration);
    this.updateResponseTimeMetrics();

    // Check performance thresholds
    this.checkPerformanceThresholds(metrics);

    this.log('Request ended', {
      id: context.id,
      duration,
      status: (response as { status?: number })?.status,
    });
  }

  /**
   * Track retry attempt
   */
  public trackRetryAttempt(
    context: RequestContext,
    attempt: RetryAttempt
  ): void {
    if (!this.config.enableRetryTracking) return;

    const metrics = this.metricsStorage.get(context.id);
    if (!metrics) return;

    const retryMetrics: IntegrationRetryAttemptMetrics = {
      attemptNumber: attempt.attemptNumber,
      startTime: attempt.timestamp,
      delay: attempt.delay,
      reason: attempt.error.message,
      error: attempt.error.name,
    };

    metrics.retryAttempts.push(retryMetrics);

    // Add timeline event
    metrics.timeline.push({
      timestamp: attempt.timestamp,
      type: 'retry_start',
      metadata: {
        attemptNumber: attempt.attemptNumber,
        delay: attempt.delay,
        reason: attempt.error.message,
      },
    });

    this.snapshot.retriedRequests++;

    this.log('Retry attempt tracked', {
      id: context.id,
      attempt: attempt.attemptNumber,
      delay: attempt.delay,
    });
  }

  /**
   * Track circuit breaker event
   */
  public trackCircuitBreakerEvent(
    requestId: string,
    state: CircuitBreakerState,
    reason: string
  ): void {
    if (!this.config.enableCircuitBreakerMetrics) return;

    const metrics = this.metricsStorage.get(requestId);
    if (!metrics) return;

    const event: IntegrationCircuitBreakerEvent = {
      timestamp: dayjs().valueOf(),
      state: state.state,
      endpoint: state.endpoint,
      reason,
      failureCount: state.failureCount,
    };

    metrics.circuitBreakerEvents.push(event);

    // Add timeline event
    metrics.timeline.push({
      timestamp: event.timestamp,
      type: 'circuit_breaker',
      metadata: {
        state: state.state,
        endpoint: state.endpoint,
        reason,
        failureCount: state.failureCount,
      },
    });

    if (state.state === 'open') {
      this.snapshot.circuitBreakerTrips++;
    }

    this.log('Circuit breaker event', {
      requestId,
      state: state.state,
      reason,
    });
  }

  /**
   * Track authentication event
   */
  public trackAuthEvent(
    requestId: string,
    type: IntegrationAuthEvent['type'],
    duration?: number,
    error?: string
  ): void {
    const metrics = this.metricsStorage.get(requestId);
    if (!metrics) return;

    const authEvent: IntegrationAuthEvent = {
      timestamp: dayjs().valueOf(),
      type,
      duration,
      error,
    };

    metrics.authenticationEvents.push(authEvent);

    // Add timeline event
    metrics.timeline.push({
      timestamp: authEvent.timestamp,
      type: type.startsWith('refresh') ? 'auth_start' : 'auth_end',
      duration,
      metadata: { type, error },
    });

    if (type === 'refresh_success') {
      this.snapshot.authRefreshCount++;
    }

    this.log('Auth event tracked', { requestId, type, duration });
  }

  /**
   * Track deduplication event
   */
  public trackDeduplication(
    requestId: string,
    wasDeduplicated: boolean,
    cacheKey?: string,
    subscribers = 0,
    cacheDuration = 0
  ): void {
    const metrics = this.metricsStorage.get(requestId);
    if (!metrics) return;

    metrics.deduplicationMetrics = {
      wasDeduplicated,
      cacheKey,
      subscribers,
      cacheDuration,
    };

    // Add timeline event
    metrics.timeline.push({
      timestamp: dayjs().valueOf(),
      type: 'deduplication',
      metadata: {
        wasDeduplicated,
        cacheKey,
        subscribers,
        cacheDuration,
      },
    });

    if (wasDeduplicated) {
      this.snapshot.deduplicationSavings++;
    }

    this.log('Deduplication tracked', {
      requestId,
      wasDeduplicated,
      subscribers,
    });
  }

  /**
   * Get performance data for a specific request
   */
  public getPerformanceData(
    requestId: string
  ): DetailedPerformanceMetrics | null {
    return this.metricsStorage.get(requestId) || null;
  }

  /**
   * Get all performance metrics
   */
  public getAllPerformanceData(): DetailedPerformanceMetrics[] {
    return Array.from(this.metricsStorage.values());
  }

  /**
   * Get current performance snapshot
   */
  public getPerformanceSnapshot(): PerformanceSnapshot {
    return { ...this.snapshot };
  }

  /**
   * Clear performance data
   */
  public clearPerformanceData(): void {
    this.metricsStorage.clear();
    this.responseTimes = [];
    this.resetSnapshot();
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<PerformanceIntegrationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Cleanup and destroy
   */
  public destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.clearPerformanceData();
  }

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  private calculateResponseSize(response?: unknown): number {
    if (!response) return 0;

    try {
      const data = (response as { data?: unknown })?.data || response;
      if (typeof data === 'string') return data.length;
      if (typeof data === 'object') return JSON.stringify(data).length;
      return 0;
    } catch {
      return 0;
    }
  }

  private updateResponseTimeMetrics(): void {
    if (this.responseTimes.length === 0) return;

    // Sort for percentile calculation
    const sorted = [...this.responseTimes].sort((a, b) => a - b);

    // Calculate average
    const sum = sorted.reduce((acc, time) => acc + time, 0);
    this.snapshot.averageResponseTime = sum / sorted.length;

    // Calculate percentiles
    this.snapshot.p50ResponseTime = this.calculatePercentile(sorted, 50);
    this.snapshot.p95ResponseTime = this.calculatePercentile(sorted, 95);
    this.snapshot.p99ResponseTime = this.calculatePercentile(sorted, 99);

    // Keep only recent response times to manage memory
    if (
      this.config.memoryEfficientStorage &&
      sorted.length > this.config.maxStoredRequests
    ) {
      this.responseTimes = sorted.slice(-this.config.maxStoredRequests);
    }
  }

  private calculatePercentile(sorted: number[], percentile: number): number {
    const index = Math.ceil((percentile / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)] || 0;
  }

  private checkPerformanceThresholds(
    metrics: DetailedPerformanceMetrics
  ): void {
    if (!metrics.duration) return;

    const { warning, critical } = this.config.performanceThresholds;

    if (metrics.duration >= critical && this.config.onPerformanceCritical) {
      this.config.onPerformanceCritical(metrics);
    } else if (
      metrics.duration >= warning &&
      this.config.onPerformanceWarning
    ) {
      this.config.onPerformanceWarning(metrics);
    }
  }

  private startCleanupTimer(): void {
    if (!this.config.metricsRetentionMs) return;

    this.cleanupTimer = setInterval(() => {
      this.cleanupOldMetrics();
    }, this.config.metricsRetentionMs / 2); // Clean up twice as often as retention
    // Don't let the cleanup timer keep a Node/test event loop alive (no-op on RN/browser).
    (this.cleanupTimer as unknown as { unref?: () => void }).unref?.();
  }

  private cleanupOldMetrics(): void {
    const cutoffTime = dayjs().valueOf() - this.config.metricsRetentionMs;

    for (const [requestId, metrics] of this.metricsStorage.entries()) {
      if (metrics.startTime < cutoffTime) {
        this.metricsStorage.delete(requestId);
      }
    }

    this.log('Old metrics cleaned up', {
      remaining: this.metricsStorage.size,
    });
  }

  private resetSnapshot(): void {
    this.snapshot = {
      timestamp: dayjs().valueOf(),
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      retriedRequests: 0,
      averageResponseTime: 0,
      p50ResponseTime: 0,
      p95ResponseTime: 0,
      p99ResponseTime: 0,
      circuitBreakerTrips: 0,
      authRefreshCount: 0,
      deduplicationSavings: 0,
    };
  }

  private log(message: string, data?: unknown): void {
    if (this.config.enableDetailedMetrics) {
      console.log(`[PerformanceIntegration] ${message}`, data || '');
    }
  }
}

// =============================================================================
// FACTORY FUNCTIONS
// =============================================================================

/**
 * Create a PerformanceIntegration instance
 */
export function createPerformanceIntegration(
  config?: Partial<PerformanceIntegrationConfig>
): PerformanceIntegration {
  return new PerformanceIntegration(config);
}

/**
 * Create performance monitoring hooks for request coordinator
 */
export function createPerformanceHooks(integration: PerformanceIntegration): {
  onRequestStart: (context: RequestContext) => void;
  onRequestEnd: (context: RequestContext, response?: unknown) => void;
  onRetryAttempt: (context: RequestContext, attempt: RetryAttempt) => void;
  onCircuitBreakerEvent: (
    requestId: string,
    state: CircuitBreakerState,
    reason: string
  ) => void;
  onAuthEvent: (
    requestId: string,
    type: IntegrationAuthEvent['type'],
    duration?: number,
    error?: string
  ) => void;
  onDeduplication: (
    requestId: string,
    wasDeduplicated: boolean,
    cacheKey?: string,
    subscribers?: number,
    cacheDuration?: number
  ) => void;
} {
  return {
    onRequestStart: (context: RequestContext): void =>
      integration.trackRequestStart(context),
    onRequestEnd: (context: RequestContext, response?: unknown): void =>
      integration.trackRequestEnd(context, response),
    onRetryAttempt: (context: RequestContext, attempt: RetryAttempt): void =>
      integration.trackRetryAttempt(context, attempt),
    onCircuitBreakerEvent: (
      requestId: string,
      state: CircuitBreakerState,
      reason: string
    ): void => integration.trackCircuitBreakerEvent(requestId, state, reason),
    onAuthEvent: (
      requestId: string,
      type: IntegrationAuthEvent['type'],
      duration?: number,
      error?: string
    ): void => integration.trackAuthEvent(requestId, type, duration, error),
    onDeduplication: (
      requestId: string,
      wasDeduplicated: boolean,
      cacheKey?: string,
      subscribers = 0,
      cacheDuration = 0
    ): void =>
      integration.trackDeduplication(
        requestId,
        wasDeduplicated,
        cacheKey,
        subscribers,
        cacheDuration
      ),
  };
}

/**
 * Calculate network timing metrics from performance entries
 */
export function calculateNetworkMetrics(
  url: string
): IntegrationNetworkMetrics | null {
  try {
    // Use Performance API if available (browser environment)
    if (typeof performance !== 'undefined' && performance.getEntriesByName) {
      const entries = performance.getEntriesByName(url);
      const entry = entries[entries.length - 1] as PerformanceResourceTiming;

      if (entry) {
        return {
          dnsLookupTime: entry.domainLookupEnd - entry.domainLookupStart,
          connectTime: entry.connectEnd - entry.connectStart,
          tlsHandshakeTime: entry.connectEnd - entry.secureConnectionStart,
          firstByteTime: entry.responseStart - entry.requestStart,
          downloadTime: entry.responseEnd - entry.responseStart,
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format metrics for display
 */
export function formatMetrics(metrics: DetailedPerformanceMetrics): string {
  const lines = [
    `Request ${metrics.requestId}:`,
    `  Duration: ${metrics.duration}ms`,
    `  Retries: ${metrics.retryAttempts.length}`,
    `  Circuit Breaker Events: ${metrics.circuitBreakerEvents.length}`,
    `  Auth Events: ${metrics.authenticationEvents.length}`,
    `  Timeline Events: ${metrics.timeline.length}`,
  ];

  if (metrics.deduplicationMetrics?.wasDeduplicated) {
    lines.push(
      `  Deduplicated: ${metrics.deduplicationMetrics.subscribers} subscribers`
    );
  }

  return lines.join('\n');
}

/**
 * Export metrics to JSON
 */
export function exportMetrics(metrics: DetailedPerformanceMetrics[]): string {
  return JSON.stringify(metrics, null, 2);
}

// =============================================================================
// EXPORTS
// =============================================================================

export default PerformanceIntegration;
